import React from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

/**
 * Asked when leaving the PowerPoint template editor with unsaved edits.
 * @param {object} props - Props.
 * @param {boolean} props.open - Visible.
 * @param {boolean} [props.saving] - A save is running (buttons disabled).
 * @param {boolean} [props.canSave] - Offer "Save and leave".
 * @param {function(): void} props.onSave - Save, then leave.
 * @param {function(): void} props.onDiscard - Leave without saving.
 * @param {function(): void} props.onStay - Stay on the page.
 * @returns {React.ReactElement}
 */
function UnsavedChangesDialog({ open, saving = false, canSave = true, onSave, onDiscard, onStay }) {
  return (
    <Dialog
      open={open}
      onClose={() => !saving && onStay()}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus
      aria-labelledby="ppt-unsaved-title"
      aria-describedby="ppt-unsaved-body"
    >
      <DialogTitle id="ppt-unsaved-title">Leave with unsaved changes?</DialogTitle>
      <DialogContent>
        <DialogContentText id="ppt-unsaved-body">
          This template has changes that are not saved yet, including any AI prompts you edited.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onStay} disabled={saving}>Keep editing</Button>
        <Button onClick={onDiscard} color="error" disabled={saving}>Discard changes</Button>
        {canSave && (
          <Button
            onClick={onSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save and leave'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UnsavedChangesDialog;
